<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import LabelsSettingsTabs from './LabelsSettingsTabs.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LimitBanner from 'dashboard/components-next/billing/LimitBanner.vue';
import { MarkdownToHtmlConverter } from './MarkdownToHtmlConverter.js';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const addLabelRef = ref(null);
const editLabelRef = ref(null);

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);
const labelLimit = computed(() => {
  const limits = currentAccount.value?.limits;
  return limits?.labels || {};
});
const labelConsumed = computed(() => records.value.length);
const labelAllowed = computed(() => labelLimit.value.allowed || 0);
const labelLimitReached = computed(
  () => labelAllowed.value > 0 && labelConsumed.value >= labelAllowed.value
);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const dismissAddPopup = () => {
  showAddPopup.value = false;
};
const cancelAddPopup = () => {
  addLabelRef.value?.resetForm();
  showAddPopup.value = false;
};
const handleAddModalClose = reason => {
  if (reason === 'cancel') cancelAddPopup();
  else dismissAddPopup();
};

const openEditPopup = response => {
  selectedLabel.value = response;
  showEditPopup.value = true;
};
const dismissEditPopup = () => {
  showEditPopup.value = false;
};
const cancelEditPopup = () => {
  editLabelRef.value?.resetForm();
  showEditPopup.value = false;
};
const handleEditModalClose = reason => {
  if (reason === 'cancel') cancelEditPopup();
  else dismissEditPopup();
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const parseMarkdownDescription = description => {
  if (!description || !description.trim()) {
    return '';
  }
  return MarkdownToHtmlConverter.convertToHtml(description);
};

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        feature-name="labels"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            md
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            :disabled="labelLimitReached"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <LabelsSettingsTabs />
    </template>
    <template #body>
      <LimitBanner
        v-if="labelAllowed > 0"
        :consumed="labelConsumed"
        :allowed="labelAllowed"
        resource-key="LABELS"
      />
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11 text-sm"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="(label, index) in records" :key="label.title">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ label.title }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div
                v-if="label.description && label.description.trim()"
                class="prose prose-sm max-w-none text-slate-600 dark:text-slate-400"
                v-html="parseMarkdownDescription(label.description)"
              />
              <span v-else class="text-slate-400 dark:text-slate-500 italic">
                {{ $t('LABEL_MGMT.LIST.NO_DESCRIPTION') }}
              </span>
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center">
                <span
                  class="w-4 h-4 mr-1 border border-solid rounded rtl:mr-0 rtl:ml-1 border-n-weak"
                  :style="{ backgroundColor: label.color }"
                />
                {{ label.color }}
              </div>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen-line"
                  slate
                  md
                  faded
                  :is-loading="loading[label.id]"
                  @click="openEditPopup(label)"
                />
                <Button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  md
                  ruby
                  faded
                  :is-loading="loading[label.id]"
                  @click="openDeletePopup(label, index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal
      v-model:show="showAddPopup"
      :destroy-on-close="false"
      :on-close="handleAddModalClose"
    >
      <AddLabel ref="addLabelRef" @close="cancelAddPopup" />
    </woot-modal>

    <woot-modal
      v-model:show="showEditPopup"
      :destroy-on-close="false"
      :on-close="handleEditModalClose"
    >
      <EditLabel
        ref="editLabelRef"
        :selected-response="selectedLabel"
        @close="cancelEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>

<style lang="scss" scoped>
// Custom styling for markdown content in table cells
::v-deep .prose {
  max-width: none;

  p {
    margin: 0.25rem 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.5rem 0 0.25rem 0;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.125rem;
  }

  h2 {
    font-size: 1.0625rem;
  }

  h3 {
    font-size: 1rem;
  }

  h4,
  h5,
  h6 {
    font-size: 0.9375rem;
  }

  ul,
  ol {
    margin: 0.25rem 0;
    padding-left: 1rem;
  }

  li {
    margin: 0.125rem 0;
  }

  code {
    background-color: rgba(97, 97, 97, 0.1);
    border-radius: 0.25rem;
    color: #616161;
    font-size: 0.8125rem;
    padding: 0.125em 0.25em;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  blockquote {
    border-left: 3px solid rgba(13, 13, 13, 0.1);
    margin: 0.25rem 0;
    padding-left: 0.75rem;
    font-style: italic;
  }

  a {
    color: #2563eb;
    text-decoration: underline;

    &:hover {
      color: #1d4ed8;
    }
  }

  // Dark mode adjustments
  .dark & {
    blockquote {
      border-left-color: rgba(255, 255, 255, 0.1);
    }

    code {
      background-color: rgba(255, 255, 255, 0.1);
      color: #d1d5db;
    }

    a {
      color: #60a5fa;

      &:hover {
        color: #93c5fd;
      }
    }
  }
}
</style>
